export default {
  header: {
    project: "Project:",
    architecture: "Architecture:",
    provider: "Provider:",
    file: {
      title: "File",
      load: "Load",
      save: "Save",
      export: "Export"
    },
    versions: {
      title: "Versions",
      create: "Create a version",
      viewHistory: "View history",
      management: "Version management"
    },
    documentation: {
      title: "Documentation",
      generate: "Generate documentation",
      markdown: "Markdown (.md)",
      html: "HTML (.html)"
    },
    fitView: "Fit view",
    fitViewTitle: "Fit view to canvas"
  },
  tabBar: {
    closeTab: "Close tab",
    newArchitecture: "New architecture",
    unsavedChanges: {
      title: "Unsaved changes",
      message: "This architecture contains unsaved changes. Do you really want to close it?",
      cancel: "Cancel",
      closeWithoutSaving: "Close without saving"
    }
  },
  versionHistory: {
    title: "Version History",
    empty: {
      title: "No saved versions",
      description: 'Use the "Create a version" button to save the current state'
    },
    buttons: {
      load: "Load",
      loadTitle: "Load this version",
      exportTitle: "Export this version",
      deleteTitle: "Delete this version"
    },
    stats: {
      resources: "resources",
      connections: "connections"
    },
    confirmLoad: {
      message: 'Load version "{{version}}"?\n\nThis will replace the current architecture.',
      success: '✅ Version "{{version}}" loaded successfully!'
    },
    confirmDelete: {
      message: "Delete this version?\n\nThis action is irreversible.",
      success: "🗑️ Version deleted successfully!"
    }
  },
  cost: {
    title: "Estimated Cost",
    autoUpdateTooltip: "Auto-update",
    empty: "Add resources to see the estimation",
    perMonth: "/month",
    perHour: "/hour",
    resourcesEstimated: "{{count}} estimated resource",
    resourcesEstimated_plural: "{{count}} estimated resources",
    resourcesUnsupported: "{{count}} unsupported resource",
    resourcesUnsupported_plural: "{{count}} unsupported resources",
    buttons: {
      detailedAnalysis: "Detailed Analysis",
      refresh: "Refresh",
      save: "Save",
      saving: "Saving...",
      showForecasts: "Show forecasts",
      hideForecasts: "Hide forecasts",
      exportPDF: "PDF",
      exportExcel: "Excel"
    },
    budgetAlert: "Warning: {{percent}}% of budget used",
    forecasts: {
      title: "Cost forecasts:",
      quarterly: "3 months:",
      semiAnnual: "6 months:",
      annual: "12 months:"
    },
    breakdown: {
      title: "Breakdown by resource:",
      unsupported: "Unsupported resources ({{count}})"
    },
    updatedAt: "Updated: {{date}}",
    messages: {
      savingSuccess: "Estimation saved successfully!",
      savingError: "Error saving estimation",
      exportPDFError: "Error exporting PDF",
      exportExcelError: "Error exporting Excel"
    }
  },
  analysis: {
    title: "Cost Analysis",
    subtitle: "Detailed analysis of your cloud architecture",
    kpis: {
      estimatedCost: "ESTIMATED COST",
      budgetUsage: "BUDGET USAGE",
      forecast: "FORECAST",
      potentialSavings: "POTENTIAL SAVINGS",
      vsBudget: "vs budget",
      recommendations: "recommendations"
    },
    tabs: {
      overview: "Overview",
      breakdown: "Breakdown",
      chart: "Charts",
      optimization: "Optimization"
    },
    overview: {
      resources: "Resources",
      resourcesEstimated: "Estimated resources",
      unsupported: "unsupported",
      optimization: "Optimization",
      loading: "Loading...",
      possibleSavings: "Possible savings",
      noData: "No data available"
    },
    optimization: {
      analyzing: "Analyzing possible optimizations...",
      noRecommendations: "No recommendations available",
      noRecommendationsDescription: "Add resources to get optimization recommendations"
    }
  },
  sidebar: {
    searchPlaceholder: "Search for a component...",
    loadingAzure: "Loading Azure resources…",
    errorAzure: "Azure backend error: {{error}}. (The list only displays with backend or whitelist)"
  },
  diagram: {
    save: {
      filenamePlaceholder: "filename",
      filenameTitle: "File name (without .json)",
      buttonTitle: "Save (choose name and folder)",
      button: "Save"
    },
    load: {
      buttonTitle: "Load a diagram (.json)",
      button: "Load",
      invalidJson: "Invalid JSON file.",
      unexpectedStructure: "Unexpected JSON structure: 'nodes' and 'edges' required."
    }
  },
  flowEditor: {
    toolbar: {
      save: "Save",
      saveTitle: "Save diagram",
      undo: "Undo",
      undoTitle: "Undo (Ctrl+Z)",
      redo: "Redo",
      redoTitle: "Redo (Ctrl+Y)",
      reset: "Reset",
      resetTitle: "Reset canvas",
      resetConfirm: "⚠️ Are you sure you want to reset the canvas? This action is irreversible.",
      alignLeft: "Align left",
      alignCenter: "Align center",
      alignRight: "Align right",
      addNote: "Add a note",
      note: "Note",
      addGroup: "Add a group",
      group: "Group",
      validation: "Validation",
      showValidation: "Show validation",
      hideValidation: "Hide validation",
      variables: "Variables",
      showVariables: "Show variables",
      hideVariables: "Hide variables"
    },
    contextMenu: {
      settings: "Settings",
      rename: "Rename",
      duplicate: "Duplicate",
      delete: "Delete"
    },
    defaults: {
      newNote: "New note...",
      newGroup: "New Group"
    }
  },
  settingsPanel: {
    title: "Settings — {{title}}",
    defaultTitle: "Resource",
    buttons: {
      expand: "Expand",
      expandTitle: "Expand / Settings on the right",
      close: "Close",
      save: "Save",
      clear: "Clear"
    },
    search: {
      placeholder: "🔍 Search for a field...",
      active: "Search: \"{{query}}\"",
      clear: "Clear"
    },
    fields: {
      label: "Label",
      location: "Location",
      locationPlaceholder: "Ex: eastus, westeurope",
      locationDescription: "Azure region where the resource will be deployed",
      locationNone: "No location (global resource)",
      name: "Name",
      namePlaceholder: "Resource name",
      resourceGroup: "Resource Group",
      resourceGroupPlaceholder: "Resource group name",
      selectPlaceholder: "— choose —",
      enterField: "Enter {{field}}"
    },
    parameters: {
      title: "Parameters",
      advanced: "⚙ Advanced",
      simple: "✓ Simple mode",
      estimation: "💰 Estimation parameters",
      noEditable: "No editable parameters.",
      requiredMark: "*"
    },
    messages: {
      loading: "Loading…",
      basicMode: "<strong>Basic mode:</strong> Advanced parameters require the backend. You can still modify basic parameters.",
      basicModeConfig: "<strong>Basic mode:</strong> Detailed configuration available when backend is connected",
      loadedFromTerraform: "✓ Loaded from official Terraform schema"
    }
  },
  connectionSuggestions: {
    button: {
      title: "{{count}} connection suggestion",
      title_plural: "{{count}} connection suggestions",
      dismiss: "Hide suggestions"
    },
    drawer: {
      title: "Connection Suggestions",
      close: "Close",
      stats: {
        critical: "{{count}} Critical",
        critical_plural: "{{count}} Critical",
        recommended: "{{count}} Recommended",
        recommended_plural: "{{count}} Recommended",
        total: "{{count}} Total"
      },
      noSuggestions: {
        title: "Valid Connection!",
        message: "This connection requires no additional configuration."
      },
      suggestion: {
        resourceType: "Resource type:",
        properties: "Pre-configured properties:",
        createButton: "Create resource",
        configureManually: "Configure manually",
        manualWarning: "⚠️ This suggestion requires manual property configuration"
      }
    }
  },
  validation: {
    panel: {
      success: "Validation successful",
      noProblem: "No problems detected",
      title: "Validation",
      problems: "{{count}} problem detected",
      problems_plural: "{{count}} problems detected",
      minimize: "Minimize",
      close: "Close",
      allCorrect: "All resources are correctly configured"
    },
    minimized: {
      noProblem: "No problems",
      problems: "{{count}} problem",
      problems_plural: "{{count}} problems"
    },
    groups: {
      errors: "Errors",
      warnings: "Warnings",
      info: "Information",
      field: "Field:"
    }
  },
  patterns: {
    title: "Patterns",
    library: "Library",
    openTitle: "Open patterns library - Reusable architecture templates",
    closeTitle: "Close patterns library",
    resourceCount: "{{count}} resources"
  },
  rightDrawer: {
    ariaLabel: "Settings panel (dock)"
  },
  fieldSuggestions: {
    showSuggestions: "Show suggestions",
    suggestionCount: "{{count}} suggestion",
    suggestionCount_plural: "{{count}} suggestions"
  },
  groupNode: {
    colors: {
      blue: "Blue",
      green: "Green",
      pink: "Pink",
      orange: "Orange",
      purple: "Purple",
      gray: "Gray"
    },
    edit: "Edit",
    delete: "Delete",
    dropHint: "Drag resources here to group them"
  },
  canvasWrapper: {
    messages: {
      projectLoaded: "✅ Project loaded successfully!",
      invalidJson: "❌ Invalid JSON file.",
      architectureSaved: "✅ Architecture saved successfully to the cloud (ADLS)!",
      projectSavedLocally: "💾 Project saved locally (create an architecture for cloud backup)",
      documentationGenerated: "📄 Documentation generated successfully (Markdown and HTML)!",
      saveError: "❌ Save error: {{error}}",
      versionCreated: "🔖 Version \"{{version}}\" created successfully!"
    },
    prompts: {
      versionName: "Version name:",
      versionDescription: "Version description:"
    },
    defaults: {
      architecture: "Architecture",
      architectureCloud: "Cloud Architecture",
      project: "Project"
    }
  },
  connectionSuggestionsPanel: {
    title: "Connection Suggestions",
    close: "Close",
    stats: {
      critical: "critical",
      critical_plural: "critical",
      recommended: "recommended",
      recommended_plural: "recommended"
    },
    noConfiguration: "This connection requires no additional configuration",
    resourceType: "Resource type:",
    preConfiguredProperties: "Pre-configured properties:",
    createResource: "Create resource",
    manualConfiguration: "This suggestion requires manual configuration",
    configureManually: "Configure manually"
  },
  blockEditor: {
    keyPlaceholder: "key",
    valuePlaceholder: "value",
    noAttributes: "No attributes in this block.",
    addButton: "Add",
    removeButton: "Remove",
    selectPlaceholder: "— choose —"
  },
  annotationNode: {
    save: "Save",
    cancel: "Cancel",
    placeholder: "Click to add a note..."
  },
  diagramSettings: {
    editParameters: "Edit parameters..."
  },
  variables: {
    title: "Variables & Parameters",
    parameters: "parameters",
    internal: "internal",
    minimized: "{{count}} variables",
    refresh: "Refresh",
    minimize: "Minimize",
    close: "Close",
    search: "Search variables...",
    filterAll: "All",
    filterParameters: "Parameters",
    filterVariables: "Variables",
    add: "Add",
    loading: "Loading...",
    retry: "Retry",
    noResults: "No variables found matching your search",
    empty: "No variables defined yet",
    createFirst: "Create your first variable",
    confirmDelete: "Are you sure you want to delete this variable?",
    edit: "Edit",
    delete: "Delete",
    param: "param",
    var: "var",
    sensitive: "sensitive",
    default: "Default",
    rules: "Rules",
    editTitle: "Edit Variable",
    createTitle: "Create Variable",
    basicInfo: "Basic Information",
    name: "Name",
    displayName: "Display Name",
    description: "Description",
    descriptionPlaceholder: "Describe what this variable is for...",
    scope: "Scope",
    scopeParameter: "Parameter (exposed)",
    scopeVariable: "Variable (internal)",
    scopeParameterHelp: "Users can override this value",
    scopeVariableHelp: "Internal, auto-calculated value",
    type: "Type",
    category: "Category",
    order: "Order",
    isSensitive: "Sensitive value (will be masked in logs)",
    defaultValue: "Default Value",
    staticValue: "Static Value",
    expression: "Expression",
    expressionHelp: "Use Terraform expressions like ${resource.attribute}",
    validation: "Validation Rules",
    required: "Required",
    minValue: "Min value",
    maxValue: "Max value",
    minLength: "Min length",
    maxLength: "Max length",
    pattern: "Pattern (regex)",
    allowedValues: "Allowed values",
    addValue: "Add value...",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving...",
    nameRequired: "Name is required",
    nameFormat: "Name must be snake_case (lowercase, underscores, start with letter)"
  }
};
